// src/components/MobileNavigation.tsx

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Logo from './Logo';
import NavigationThemeToggle from '../ui/NavigationThemeToggle';
import { useNavigate, useLocation } from 'react-router-dom';

interface MobileNavigationProps {
  isLoggedIn: boolean;
  onLogout: () => void;
}

const MobileNavContainer = styled.div<{ isScrolled: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${(props) => (props.isScrolled ? '1.5rem 2rem' : '1.5rem 2rem')};
  position: sticky;
  top: 0;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0);
  transition: padding 0.3s ease;

  @media (min-width: 769px) {
    display: none;
  }
`;

const LogoContainer = styled.div<{ isVisible: boolean }>`
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const HamburgerIcon = styled.div<{ isVisible: boolean }>`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.3s ease;

  span {
    height: 2px;
    width: 25px;
    background: var(--color-text);
    margin-bottom: 4px;
    border-radius: 5px;
  }
`;

const MobileNavMenu = styled.div<{ isOpen: boolean }>`
  display: none;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  padding: 2rem 0;
  transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(100%)')};
  transition: transform 0.3s ease-in-out;
  z-index: 2000;
  overflow-y: auto;

  @media (max-width: 768px) {
    display: flex;
  }

  [data-theme="light"] & {
    background: rgba(255, 255, 255, 0.9);
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 2rem;
  color: var(--color-text);
  cursor: pointer;
  z-index: 3000;
  background: rgba(0, 0, 0, 0.2);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
  transition: background 0.3s ease;

  [data-theme="light"] & {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const MobileNavItem = styled.div`
  color: var(--color-text);
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: 500;
  padding: 1rem 1.5rem;
  text-align: center;
  cursor: pointer;
  border-radius: 8px;
  margin: 0.25rem 0;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: var(--color-primary);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const MobileNavigation: React.FC<MobileNavigationProps> = ({ isLoggedIn, onLogout }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  const lastScrollYRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 50);
      
      // Determine scroll direction with a small threshold to prevent flickering
      if (Math.abs(currentScrollY - lastScrollYRef.current) > 5) {
        if (currentScrollY > lastScrollYRef.current) {
          setScrollDirection('down');
        } else if (currentScrollY < lastScrollYRef.current) {
          setScrollDirection('up');
        }
      }
      
      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.replace('#', '');
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (window.fbq && !isMobileMenuOpen) {
      window.fbq('track', 'MobileMenuOpen');
    }
  };

  const handleNavigation = (hashOrPath: string, isRoute: boolean = false) => {
    if (isRoute) {
      // Directly navigate to the route
      navigate(hashOrPath);
    } else if (location.pathname !== '/') {
      // Navigate to `/` and include the hash
      navigate(`/#${hashOrPath}`);
    } else {
      // If on the home page, directly scroll to the hash section
      const element = document.getElementById(hashOrPath);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.location.hash = `#${hashOrPath}`; // Update hash if element not found
      }
    }
    setIsMobileMenuOpen(false); // Close the mobile menu
  };

  const renderMobileNavItems = () => {
    // Filter out Features, FAQ, Newsletter on login and signup pages
    const isAuthPage = location.pathname === '/login' || location.pathname === '/signup';
    
    const commonNavItems = [
      { id: 'home', label: 'Home', hash: 'home' },
      ...(isAuthPage ? [] : [
        { id: 'features', label: 'Features', hash: 'features' },
        { id: 'faq', label: 'FAQ', hash: 'faq' },
        { id: 'newsletter', label: 'Newsletter', hash: 'newsletter' },
      ]),
      { id: 'about-us', label: 'About Us', path: '/about-us' },
      { id: 'help-center', label: 'Help Center', path: '/help-center' },
      { id: 'download', label: 'Download', path: '/download' },
    ];

    return (
      <>
        {isLoggedIn ? (
          <>
            <MobileNavItem onClick={() => handleNavigation('/account', true)}>Account</MobileNavItem>
            {commonNavItems.map((item) =>
              item.hash ? (
                <MobileNavItem key={item.id} onClick={() => handleNavigation(item.hash)}>
                  {item.label}
                </MobileNavItem>
              ) : (
                <MobileNavItem
                  key={item.id}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    navigate(item.path || '/');
                  }}
                >
                  {item.label}
                </MobileNavItem>
              )
            )}
            <MobileNavItem onClick={onLogout}>Logout</MobileNavItem>
          </>
        ) : (
          <>
            {/* Show "Sign Up" on login page, "Login" on signup page */}
            <MobileNavItem 
              onClick={() => {
                const isLoginPage = location.pathname === '/login';
                navigate(isLoginPage ? '/signup' : '/login');
              }}
            >
              {location.pathname === '/login' ? 'Sign Up' : 'Login'}
            </MobileNavItem>
            {commonNavItems.map((item) =>
              item.hash ? (
                <MobileNavItem key={item.id} onClick={() => handleNavigation(item.hash)}>
                  {item.label}
                </MobileNavItem>
              ) : (
                <MobileNavItem
                  key={item.id}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    navigate(item.path || '/');
                  }}
                >
                  {item.label}
                </MobileNavItem>
              )
            )}
          </>
        )}
      </>
    );
  };

  return (
    <>
      <MobileNavContainer isScrolled={isScrolled}>
        <LogoContainer isVisible={scrollDirection === 'up' || !isScrolled}>
          <Logo />
        </LogoContainer>
        <HamburgerIcon isVisible={scrollDirection === 'up' || !isScrolled} onClick={toggleMobileMenu}>
          <span></span>
          <span></span>
          <span></span>
        </HamburgerIcon>
      </MobileNavContainer>
      {isMobileMenuOpen && (
        <MobileNavMenu isOpen={isMobileMenuOpen}>
          <CloseButton onClick={() => setIsMobileMenuOpen(false)}>&times;</CloseButton>
          <div 
            style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'center', 
              height: '100%',
              alignItems: 'center',
              gap: '1rem'
            }}
            onClick={(e) => {
              // Close menu when clicking on empty space (not on buttons)
              if (e.target === e.currentTarget) {
                setIsMobileMenuOpen(false);
              }
            }}
          >
            {/* Theme Toggle for Mobile */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              marginBottom: '1rem',
              padding: '0.5rem'
            }}>
              <NavigationThemeToggle />
            </div>
            {renderMobileNavItems()}
          </div>
        </MobileNavMenu>
      )}
    </>
  );
};

export default MobileNavigation;
